import {
  Box,
  Divider,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  faCopy,
  faCube,
  faEllipsisH,
  faEyeSlash,
  faPen,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { fromPairs, isEmpty } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useMutation } from 'urql';
import { useTranslation } from 'react-i18next';
import {
  DeleteOrderDocument,
  OrderDeepFragment,
  UpdateOrderDocument,
  SharedLinkDeepFragment,
  DeleteSharedLinkDocument,
  EventResource,
  EventType,
  ShapeFolderDeepFragment,
  PredicateType,
  ColumnType,
  AggregatedOrderFilterType,
} from '../../../gql/graphql';
import {
  ResolvedDbIdsReturn,
  orderStatusColorMapper,
} from '../../../hooks/order';
import { useVisibility } from '../../../services/viewer/visibility';
import CircleIcon from '../../common/CircleIcon';
import ConfirmDeletionModal from '../../common/ConfirmDeletionModal';
import { DbIdsPerModel, useViewer } from '../../common/ForgeViewer';
import SparkelIcon from '../../common/icon/SparkelIcon';
import CopyOrderModal from '../../orderContractor/CopyOrderModal';
import { getViewTypeIcon } from '../aggregated-order-panel/AggregatedOrderPanel';
import { useDiffing } from '../project-history/hooks/useDiffing';
import { DiffBadge } from '../project-history/DiffBadge';
import { DiffText } from '../project-history/DiffText';
import ManageSharedLinksPopover from './ManageSharedLinksPopover';
import { useEventHistory } from 'src/hooks/event-history';
import { useShapeFolders } from 'src/hooks/shape-folders';

type OrderItemProps = {
  order: OrderDeepFragment;
  orders: OrderDeepFragment[];
  resolvedDbIds: ResolvedDbIdsReturn;
  isActive: boolean;
  renameOnLoad?: boolean;
  readOnly: boolean;
  projectSharedLinks: SharedLinkDeepFragment[];
  isProtan: boolean;
  shapeFolders: ShapeFolderDeepFragment[] | undefined;
  isDiffing?: boolean;
  diff?: OrderItemDiff;
  onRenameComplete?: (orderId: string) => void;
};

export type OrderItemDiff = {
  eventType?: EventType;
  oldName?: string;
  newName?: string;
};
// eslint-disable-next-line complexity
export default function OrderItem({
  order,
  orders,
  resolvedDbIds,
  isActive,
  renameOnLoad,
  readOnly,
  projectSharedLinks,
  isProtan,
  shapeFolders,
  isDiffing,
  diff,
  onRenameComplete,
}: OrderItemProps): React.ReactElement {
  const {
    isOpen: alertModalIsOpen,
    onOpen: openAlertModal,
    onClose: closeAlertModal,
  } = useDisclosure();
  const { loadedModels, isAllModelsLoaded } = useViewer();

  const [, updateOrder] = useMutation(UpdateOrderDocument);

  const [isRenaming, setIsRenaming] = useState(renameOnLoad);
  const [newName, setNewName] = useState(order.name);
  const [isLoading, setIsLoading] = useState(false);
  const [hasDuplicateName, setHasDuplicateName] = useState(false);

  const { t } = useTranslation('project');

  const { isolate, hide } = useVisibility();

  const { logEvent } = useEventHistory(order.projectId);
  const { renameFolder, deleteFolder } = useShapeFolders();
  const { getTextColor, getBackgroundColor } = useDiffing();

  const eventType = diff?.eventType;
  let backgroundColor: string | undefined;
  let textColor: string | undefined;

  if (isDiffing && eventType) {
    backgroundColor = getBackgroundColor(eventType);
    textColor = getTextColor(eventType);
  }

  let orderLabel: React.ReactNode = order.name;
  if (isDiffing && diff?.oldName && diff?.newName) {
    orderLabel = <DiffText oldValue={diff.oldName} newValue={diff.newName} />;
  }

  const renameOrder = () => {
    setIsRenaming(true);
    setHasDuplicateName(false);
  };

  const checkForDuplicateName = (name: string) => {
    const existingOrder = orders.find(
      (otherOrder) =>
        otherOrder.id !== order.id &&
        otherOrder.name.toLowerCase() === name.toLowerCase()
    );
    setHasDuplicateName(!!existingOrder);
  };

  const submitRenaming = async () => {
    setIsRenaming(false);
    // Inform parent that renaming interaction has completed (blur or submit)
    if (onRenameComplete) {
      onRenameComplete(order.id);
    }
    if (newName === order.name) {
      setHasDuplicateName(false);
      return;
    }

    if (hasDuplicateName) {
      // Don't submit if there's a duplicate name
      return;
    }

    setIsLoading(true);

    try {
      if (!isProtan) {
        // Update the order name
        await updateOrder({
          input: {
            id: order.id,
            patch: {
              name: newName,
            },
          },
        });
      } else {
        // For Protan, also rename the corresponding shape folder if it exists and update the order filters
        if (shapeFolders) {
          const correspondingFolder = shapeFolders.find(
            (folder) => folder.name === order.name
          );

          if (correspondingFolder) {
            await renameFolder(correspondingFolder.id, newName);
          }

          const newFilters: AggregatedOrderFilterType[] =
            order.aggregatedOrderFilters?.map((filter) => {
              if (filter?.columnName === 'Title') {
                return {
                  columnName: 'Title',
                  columnType: ColumnType.Description,
                  predicate: PredicateType.Equals,
                  predicateValues: [newName],
                };
              }
              return filter;
            }) ?? [];

          // Update the order name and filter
          await updateOrder({
            input: {
              id: order.id,
              patch: {
                name: newName,
                aggregatedOrderFilters: newFilters,
              },
            },
          });

          return;
        }
      }

      logEvent({
        eventName: 'Table Renamed',
        eventType: EventType.Modified,
        resourceName: order.name,
        resourceType: EventResource.Order,
        metadata: {
          affectedResources: [order.id],
          oldName: order.name,
          newName,
        },
      });
    } catch (error) {
      console.error('Error renaming order:', error);
    } finally {
      setIsLoading(false);
      setIsRenaming(false);
      setHasDuplicateName(false);
    }
  };

  const isolateOrder = () => {
    if (
      !isEmpty(loadedModels) &&
      isAllModelsLoaded &&
      !isEmpty(order.orderEntries) &&
      !resolvedDbIds.isLoading
    ) {
      const dbIds = resolvedDbIds.getDbIdsForOrder(order.id);
      let dbIdsToIsolate: DbIdsPerModel = fromPairs(
        dbIds.map(({ modelUrn, dbIds }) => [modelUrn, dbIds])
      );
      isolate(dbIdsToIsolate);
    }
  };

  const hideOrder = () => {
    if (
      !isEmpty(loadedModels) &&
      isAllModelsLoaded &&
      !isEmpty(order.orderEntries) &&
      !resolvedDbIds.isLoading
    ) {
      const dbIds = resolvedDbIds.getDbIdsForOrder(order.id);
      let dbIdsToHide: DbIdsPerModel = fromPairs(
        dbIds.map(({ modelUrn, dbIds }) => [modelUrn, dbIds])
      );
      hide(dbIdsToHide);
    }
  };

  const [, deleteOrder] = useMutation(DeleteOrderDocument);

  const { orderId: openOrderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDeleteOrder = useCallback(async () => {
    const result = await deleteOrder({
      input: { id: order.id },
    });
    if (result.error) {
      throw result.error;
    } else {
      // For Protan, also delete the corresponding shape folder if it exists
      if (isProtan && shapeFolders) {
        const correspondingFolder = shapeFolders.find(
          (folder) => folder.name === order.name
        );

        if (correspondingFolder) {
          try {
            await deleteFolder(correspondingFolder.id);
          } catch (error) {
            console.error('Failed to delete shape folder:', error);
            // Don't fail the order deletion if shape folder deletion fails
          }
        }
      }

      logEvent({
        eventName: 'Table Deleted',
        eventType: EventType.Deleted,
        resourceName: order.name,
        resourceType: EventResource.Order,
        metadata: {
          affectedResources: [order.id],
          details: `Deleted table ${order.name}`,
        },
      });
    }
    if (order.id === openOrderId) {
      navigate(`/project/${order.projectId}/takeoff${location.search}`);
    }
  }, [
    deleteOrder,
    logEvent,
    navigate,
    location.search,
    openOrderId,
    order.id,
    order.name,
    order.projectId,
    isProtan,
    shapeFolders,
    deleteFolder,
  ]);

  const {
    isOpen: showCopyModal,
    onOpen: openCopyModal,
    onClose: closeCopyModal,
  } = useDisclosure();

  const toast = useToast();

  const activeLinksForThisOrder = useMemo(() => {
    if (!projectSharedLinks || projectSharedLinks.length === 0) {
      return [];
    }
    return projectSharedLinks.filter(
      (link) =>
        link.orderIds?.includes(order.id) &&
        new Date(link.expiresAt) > new Date()
    );
  }, [projectSharedLinks, order.id]);

  const [, deleteSharedLinkMutation] = useMutation(DeleteSharedLinkDocument);

  const handleRevokeLink = async (linkId: string) => {
    try {
      const result = await deleteSharedLinkMutation({
        input: { id: linkId },
      });
      if (result.error) {
        throw new Error(result.error.message);
      }
      toast({
        title: t('project:order-item.shared-links.toast.revoked-title'),
        description: t(
          'project:order-item.shared-links.toast.revoked-description'
        ),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err: unknown) {
      toast({
        title: t('project:order-item.shared-links.toast.revoke-error-title'),
        description:
          err instanceof Error ? err.message : t('common:unknown-error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      {isRenaming ? (
        <InputGroup size={'sm'} height={'auto'}>
          <Tooltip
            isOpen={hasDuplicateName}
            label="A table with this name already exists"
            placement="top"
            hasArrow
          >
            <Input
              aria-label="table renaming input"
              defaultValue={order.name}
              autoFocus
              isDisabled={isLoading}
              variant={'filled'}
              borderRadius={'md'}
              height={7}
              isInvalid={hasDuplicateName}
              onChange={(e) => {
                const value = e.target.value;
                setNewName(value);
                checkForDuplicateName(value);
              }}
              onFocus={(e) => e.target.select()}
              onBlur={submitRenaming}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  submitRenaming();
                }
              }}
            />
          </Tooltip>
          {isLoading ? (
            <InputRightElement>
              <Spinner color="cyan.500" size={'xs'} />
            </InputRightElement>
          ) : undefined}
        </InputGroup>
      ) : (
        <Flex
          gap={1}
          role="group"
          aria-checked={isActive}
          bgColor={backgroundColor}
          color={textColor}
          borderRadius="lg"
          _hover={{
            bgColor: 'gray.50',
            borderRadius: 'lg',
            _dark: {
              bgColor: 'gray.700',
            },
          }}
          _checked={{
            bgColor: 'blue.50',
            textColor: 'blue.700',
            fontWeight: 'bold',
            _dark: {
              bgColor: 'blue.800',
              textColor: 'blue.200',
            },
          }}
        >
          {
            <Tooltip openDelay={400} label={order.name}>
              <Link
                my={1}
                as={RouterLink}
                to={`/project/${order.projectId}/takeoff/${order.id}${location.search}`}
                noOfLines={1}
                fontSize={'sm'}
                wordBreak="break-all"
                width="100%"
                aria-label={order.name}
              >
                {order.isAggregatedOrder ? (
                  <Box mr={1} ml={1} display={'inline-block'}>
                    <SparkelIcon
                      icon={getViewTypeIcon(
                        order.aggregatedOrderView ?? undefined
                      )}
                      size="xs"
                      color={'gray.500'}
                      fixedWidth
                    />
                  </Box>
                ) : (
                  <CircleIcon
                    boxSize={3}
                    color={orderStatusColorMapper(order.status)}
                    mb={1}
                    mr={1}
                    ml={1}
                  />
                )}
                {orderLabel}
              </Link>
            </Tooltip>
          }

          <Menu closeOnSelect={false} isLazy>
            {({ isOpen }) => (
              <>
                <MenuButton
                  opacity={isOpen ? 1 : 0}
                  _groupHover={{
                    opacity: 1,
                  }}
                  ml={'auto'}
                  marginRight={activeLinksForThisOrder.length > 0 ? 0 : 1}
                  my={'auto'}
                  isRound
                  as={IconButton}
                  colorScheme="gray"
                  size={'xs'}
                  maxWidth={16}
                >
                  <SparkelIcon icon={faEllipsisH} />
                </MenuButton>

                <Portal>
                  <MenuList maxW="xs" zIndex={99}>
                    {!readOnly && (
                      <MenuItem
                        icon={<SparkelIcon icon={faPen} fixedWidth />}
                        onClick={renameOrder}
                      >
                        {t('table-item-menu.rename')}
                      </MenuItem>
                    )}
                    <Divider />
                    <MenuItem
                      isDisabled={resolvedDbIds.isLoading || !isAllModelsLoaded}
                      icon={<SparkelIcon icon={faCube} fixedWidth />}
                      onClick={isolateOrder}
                    >
                      {t('table-item-menu.isolate')}
                    </MenuItem>
                    <MenuItem
                      isDisabled={resolvedDbIds.isLoading || !isAllModelsLoaded}
                      icon={<SparkelIcon icon={faEyeSlash} fixedWidth />}
                      onClick={hideOrder}
                    >
                      {t('table-item-menu.hide')}
                    </MenuItem>
                    {!readOnly && (
                      <>
                        <Divider />
                        <MenuItem
                          onClick={openCopyModal}
                          icon={<SparkelIcon icon={faCopy} />}
                        >
                          {t('table-item-menu.duplicate')}
                        </MenuItem>
                        <MenuItem
                          icon={
                            <SparkelIcon
                              icon={faTrash}
                              color="red.400"
                              fixedWidth
                            />
                          }
                          onClick={openAlertModal}
                          textColor="red.400"
                        >
                          {t('table-item-menu.delete')}
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </Portal>
              </>
            )}
          </Menu>

          {activeLinksForThisOrder.length > 0 && !readOnly && (
            <ManageSharedLinksPopover
              orderName={order.name}
              activeLinks={activeLinksForThisOrder}
              onRevokeLink={handleRevokeLink}
            />
          )}

          {isDiffing && eventType && (
            <Box alignSelf={'center'} marginRight={1}>
              <DiffBadge eventType={eventType} />
            </Box>
          )}
        </Flex>
      )}
      <ConfirmDeletionModal
        title={t('table-item-menu.delete') + ' ' + order.name}
        isOpen={alertModalIsOpen}
        onConfirm={handleDeleteOrder}
        onClose={closeAlertModal}
      />
      <CopyOrderModal
        order={order}
        modalIsOpen={showCopyModal}
        closeModal={closeCopyModal}
      />
    </>
  );
}
